<template>
  <section>
    <header
      class="flex flex-col md:flex-row md:items-center md:justify-between"
    >
      <h1 class="heading-1">{{ i18n(translations.wishListTitle) }}</h1>
      <div class="flex flex-col gap-6 md:flex-row md:space-x-12">
        <RevButton
          class="mt-24 md:mt-0"
          variant="secondary"
          @click="handleCsvAction"
        >
          {{ i18n(translations.buttonImportExport) }}
        </RevButton>
        <RevButton
          v-if="!displayNoOffer"
          class="md:mt-0"
          :to="{
            name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
          }"
          variant="primary"
        >
          {{ i18n(translations.buttonCreate) }}
        </RevButton>
      </div>
    </header>
    <QuotesFilters
      :loading-button="data.filtersLoadingButton"
      @filter="handleFilters"
    />

    <div
      v-if="!data.isLoading && countryCodeOptions.length"
      class="mb-32 mt-68 flex flex-col md:mb-32 md:mt-44 md:flex-row"
    >
      <p class="text-static-default-mid heading-2 mb-[1.6rem] md:mb-0">
        {{ i18n(translations.savedProductsCount, { total: pagination.count }) }}
      </p>
      <RevInputSelect
        id="marketCode"
        v-model="data.marketCode"
        class="w-[16rem] md:ml-auto"
        :label="i18n(translations.marketInputLabel)"
        name="marketCode"
        :options="countryCodeOptions"
        @update:model-value="() => handleMarketCodeChange()"
      />
    </div>

    <div class="mb-72 mt-68 md:mt-44">
      <div v-if="data.isLoading" data-test="listing-skeleton">
        <div class="mb-[3.2rem] flex flex-col md:flex-row">
          <RevSkeleton
            class="text-static-default-mid heading-2 mb-[1.6rem]"
            shape="rectangle"
          >
            {{ i18n(translations.savedProductsCount, { total: 0 }) }}
          </RevSkeleton>
        </div>
        <LoadingSkeleton class="mb-52 mt-32 md:mb-36" />
        <LoadingSkeleton class="mb-52 md:mb-36" />
      </div>

      <div v-if="displayNoOffer">
        <NoOffer v-if="isMobileScreen" />
        <RevContainer v-else>
          <NoOffer />
        </RevContainer>
      </div>

      <div v-if="displayQuotations">
        <QuotesOfferDetails
          v-for="quote in data.quotes"
          :key="`${quote.product.productId}-${quote.marketCode}`"
          class="mb-52 md:mb-36"
          :loading-item="data.loadingItem"
          :quote
          @update-listing="
            saveListings(quote.product.productId, quote.marketCode, $event)
          "
        />
        <Pagination
          v-if="displayPagination"
          class="flex flex-1 lg:mt-0"
          :current-page="pagination?.currentPage"
          :page-count="pages"
          @click="handlePaginationUpdate"
        />
      </div>
    </div>
    <CsvModal
      :is-importing="data.isModalActionLoading"
      :name="MODAL_NAMES.REFURBISHER_WISHLIST_CSV"
      @submit-export="handleCsvExport"
      @submit-import="handleCsvImport"
    />
  </section>
  <QuotesTimeoutToast v-model="isTimeoutToastOpen" data-test="timeout-toast" />
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, reactive, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import { getBuybackQuotes } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes'
import type {
  BuyBackQuote,
  PostBuyBackQuoteListing,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import { getMerchant } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import {
  postListingsExport,
  postListingsImport,
} from '@backmarket/http-api/src/api-specs-catalog/buyback-catalog'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import MODAL_NAMES from '@backmarket/nuxt-layer-buyback/utils/modals'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { sleep } from '@backmarket/utils/async/sleep'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevInputSelect } from '@ds/components/InputSelect'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'

import { VALUE_FILTER_ALL } from '~/constants/buyback'
import { ROUTE_NAMES } from '~/constants/routes'
import QuotesTimeoutToast from '~/scopes/buyback/components/QuotesTimeoutToast/QuotesTimeoutToast.vue'
import NoOffer from '~/scopes/buyback/pages/wishlist/components/NoOffer/NoOffer.vue'

import {
  BUYBACK_QUOTES_FETCH_INTERVAL,
  BUYBACK_QUOTES_FETCH_TIMEOUT,
} from '../../constants'
import {
  getCountryCodeFromLocale,
  matchPostAndGetListings,
} from '../../utils/buyback.utils'

import type { QuotesWishListData } from './QuotesWishList.types'
import type { Pagination } from './WishList.types'
import translations from './Wishlist.translations'
import type {
  CsvExportFormValues,
  CsvImportFormValues,
} from './components/CsvModal/CsvModal.types'
import CsvModal from './components/CsvModal/CsvModal.vue'
import type { FilterEventPayload } from './components/Filters/Filters.types'
import QuotesFilters from './components/Filters/QuotesFilters.vue'
import LoadingSkeleton from './components/LoadingSkeleton/LoadingSkeleton.vue'
import QuotesOfferDetails from './components/OfferDetails/QuotesOfferDetails.vue'
import { useUpdateListings } from './composables/useUpdateListings'

const i18n = useI18n()
const logger = useLogger()
const breakpoint = useClientOnlyComputedBreakpoint()
const { closeToast, openErrorToast, openSuccessToast } = useTheToast()

const isTimeoutToastOpen = ref(false)

const data = reactive<QuotesWishListData>({
  availableMarkets: [],
  quotes: [],
  marketCode: VALUE_FILTER_ALL,
  filters: {},
  isToastOpened: false,
  filtersLoadingButton: '',
  isLoading: true,
  isModalActionLoading: false,
  loadingItem: {
    productId: '',
    grades: [],
  },
})

const pagination = reactive<Pagination>({
  pageSize: 10,
  currentPage: 1,
  count: 0,
  previous: null,
  next: null,
})

const [
  { data: quotesResponse, error: quotesError },
  { data: marketsResponse, error: marketsError },
] = await Promise.all([
  useHttpFetch(getBuybackQuotes, {
    queryParams: {
      pageNumber: pagination.currentPage,
      pageSize: pagination.pageSize,
    },
  }),
  useHttpFetch(getMerchant),
])

if (quotesError.value) {
  openErrorToast({
    content: getApiErrorMessage(quotesError.value as unknown as Error),
  })

  logger.error('[BUYBACK][API][QUOTES] Error getting quotes', {
    error: quotesError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

if (marketsError.value) {
  openErrorToast({
    content: getApiErrorMessage(marketsError.value as unknown as Error),
  })
  logger.error('[BUYBACK][API][QUOTES] Error getting markets', {
    error: marketsError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

if (quotesResponse.value && marketsResponse.value) {
  pagination.count = quotesResponse.value.totalCount
  data.quotes = quotesResponse.value.quotes

  data.availableMarkets = marketsResponse.value.markets.map((locale) => {
    const value = getCountryCodeFromLocale(locale) as string

    return {
      label: value,
      value,
    }
  })
}

data.isLoading = false

const isMobileScreen = computed(() => breakpoint.value < Breakpoint.MD)
const displayNoOffer = computed(() => !data.isLoading && isEmpty(data.quotes))
const displayQuotations = computed(
  () => !data.isLoading && !isEmpty(data.quotes),
)
const displayPagination = computed(() => pagination.count > pagination.pageSize)
const cleanedFiltersPayload = computed(() =>
  Object.entries(data.filters).reduce((acc, filters) => {
    const [key, value] = filters

    if (isEmpty(value) || value === VALUE_FILTER_ALL) {
      return {
        ...acc,
      }
    }

    if (key === 'grade') {
      return {
        ...acc,
        grades: [value],
      }
    }

    // trim() input values to prevent backends errors
    return {
      ...acc,
      [key]: value.trim(),
    }
  }, {}),
)
const countryCodeOptions = computed(() => [
  { label: i18n(translations.allMarkets), value: VALUE_FILTER_ALL },
  ...data.availableMarkets,
])
const pages = computed(() => Math.ceil(pagination.count / pagination.pageSize))

function handleCsvAction() {
  openModal(MODAL_NAMES.REFURBISHER_WISHLIST_CSV)
}

const payloadMarketCode = computed(() =>
  data.marketCode && data.marketCode !== VALUE_FILTER_ALL
    ? { marketCodes: data.marketCode }
    : {},
)

async function getQuotes({ page = 1, fullReload = true } = {}): Promise<
  BuyBackQuote[] | null
> {
  data.isLoading = fullReload

  try {
    const { totalCount, quotes } = await $httpFetch(getBuybackQuotes, {
      queryParams: {
        ...cleanedFiltersPayload.value,
        ...payloadMarketCode.value,
        pageNumber: page,
        pageSize: pagination.pageSize,
      },
    })

    pagination.count = totalCount
    pagination.currentPage = page

    return quotes
  } catch (error) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error getting quotes', {
      error: error as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })

    return null
  } finally {
    data.isLoading = false
  }
}

let startTime: number | null = null
async function pollUpdatedListings(
  productId: string,
  marketCode: string,
  listings: PostBuyBackQuoteListing[],
) {
  if (!startTime) {
    startTime = Date.now()
  }

  const upToDateQuotes = await getQuotes({
    page: pagination.currentPage,
    fullReload: false,
  })

  if (upToDateQuotes === null) {
    return
  }

  const upToDateQuote = upToDateQuotes.find(
    (quote) =>
      quote.product.productId === productId && quote.marketCode === marketCode,
  )

  if (!upToDateQuote) {
    return
  }

  if (Date.now() - startTime > BUYBACK_QUOTES_FETCH_TIMEOUT) {
    closeToast()
    startTime = null
    isTimeoutToastOpen.value = true
    data.quotes = upToDateQuotes

    return
  }

  if (!matchPostAndGetListings(listings, upToDateQuote.listings)) {
    await sleep(BUYBACK_QUOTES_FETCH_INTERVAL)
    await pollUpdatedListings(productId, marketCode, listings)

    return
  }

  startTime = null
  data.quotes = upToDateQuotes
  openSuccessToast()
}

async function saveListings(
  productId: string,
  marketCode: string,
  listings: PostBuyBackQuoteListing[],
) {
  data.loadingItem.productId = productId
  data.loadingItem.grades = listings.map((listing) => listing.grade)

  const { shouldFetch } = await useUpdateListings({
    listings,
    productId,
    marketCode,
  })

  if (shouldFetch) {
    await pollUpdatedListings(productId, marketCode, listings)
  }

  data.loadingItem.productId = ''
  data.loadingItem.grades = []
}

async function handleFilters(args: FilterEventPayload) {
  const { actionButton, filters } = args
  const { publicationState, ...rest } = filters

  data.filtersLoadingButton = String(actionButton)
  data.filters = {
    ...data.filters,
    state: publicationState,
    ...rest,
  }

  data.quotes = await getQuotes()
  data.filtersLoadingButton = ''
}

async function handlePaginationUpdate(page: { page: number }) {
  data.quotes = await getQuotes(page)
}

async function handleMarketCodeChange() {
  data.quotes = await getQuotes()
  data.filtersLoadingButton = ''
}

async function handleCsvImport(values: CsvImportFormValues) {
  data.isModalActionLoading = true
  const { encoding, csvFile, delimiter } = values

  const body = new FormData()

  body.append('csvFile', csvFile[0])
  body.append('encoding', encoding)
  body.append('delimiter', delimiter)

  try {
    const response = await $httpFetch(postListingsImport, {
      body,
    })

    openSuccessToast({
      title: i18n(translations.toastExportSuccessTitle),
      content: i18n(translations.toastExportSuccessMessage, {
        taskNumber: response.task,
      }),
    })
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error importing listings', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    data.isModalActionLoading = false
    closeModal()
  }
}

async function handleCsvExport(values: CsvExportFormValues) {
  data.isModalActionLoading = true
  const { encoding } = values

  try {
    const response = await $httpFetch(postListingsExport, {
      body: {
        encoding,
      },
    })

    openSuccessToast({
      title: i18n(translations.toastExportSuccessTitle),
      content: i18n(translations.toastExportSuccessMessage, {
        taskNumber: response.task,
      }),
    })
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error exporting listings', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    data.isModalActionLoading = false
    closeModal()
  }
}
</script>
